import { processingStatusSchema, type ProcessingStatus } from '@/types/messages/base';
import { messageAttributesSchema, type MessageAttributes } from '@/types/messages/catalog';
import { type ReferenceData } from '@/types/messages/reference';
import { isSameUserId } from '@/utils/isSameUserId';

export { processingStatusSchema, messageAttributesSchema };
export type { ProcessingStatus, MessageAttributes };

export type MessageFiles = MessageAttributes['files'];

/**
 * Minimal message shape used by getMessageReference and shouldRenderReferenceForViewer.
 *
 * The attribute-derived half is picked from `MessageAttributes` so these fields
 * cannot drift from the schema that produces them; the rest are GiftedChat
 * runtime fields with no attribute equivalent, and stay declared here.
 *
 * A `type: 'reference'` message renders as a standalone reference card. These
 * are real Twilio messages parsed by `parseMessage`, not synthesised on the
 * client; `useChatMessages` gates who sees one via `shouldRenderReferenceForViewer`.
 */
export type MessageWithOptionalReference = Pick<
  MessageAttributes,
  | 'files'
  | 'type'
  | 'draft'
  | 'contentRole'
  | 'source'
  | 'reference'
  | 'extractedQuestions'
  | 'primaryQuestionIndex'
  | 'approved'
  | 'audience'
  | 'audienceUserIds'
> & {
  messageId: string;
  user?: { _id?: string | number };
  isAutoReference?: boolean;
  _id?: string | number;
  text?: string;
  createdAt?: Date | number;
};

/**
 * Returns true if the message was sent by the current user.
 * Reusable for gating message-specific UI, e.g. auto-attached refs only for recipients.
 */
export function isMessageFromCurrentUser(
  message: { user?: { _id?: string | number } } | undefined,
  currentUserId: string | undefined
): boolean {
  return isSameUserId(message?.user?._id, currentUserId);
}

/**
 * Extracts reference data for reference-only messages, where the entire bubble shows the reference.
 */
export function getMessageReference(
  message: MessageWithOptionalReference | undefined
): ReferenceData | undefined {
  if (message?.type === 'reference' && message?.reference) {
    return message.reference;
  }

  return undefined;
}

/**
 * Determines whether a reference-only message should render for the current viewer.
 *
 * Source-less reference messages with empty text are the shape the retired
 * backend auto-attach pipeline produced (historical messages still carry it,
 * some tagged `source: 'auto'`, which the attributes schema now degrades to
 * an absent source). Those were recipient-only, so they stay hidden from
 * their sender; expert-shared references always carry `source: 'suggested'`
 * and render for both sides.
 */
export function shouldRenderReferenceForViewer(
  message: MessageWithOptionalReference | undefined,
  currentUserId: string | undefined
): boolean {
  const reference = getMessageReference(message);

  if (!reference) {
    return false;
  }

  const isCurrentUserMessage = isSameUserId(message?.user?._id, currentUserId);

  const hasEmptyText = !message?.text || message.text.trim().length === 0;

  const isAutoReferenceForSender = isCurrentUserMessage && !message?.source && hasEmptyText;

  return !isAutoReferenceForSender;
}
